<template>
  <div class="page">
    <header class="topbar">
      <AppLogo asLink :to="homePath" :height="22" />
      <div class="topbar-right">
        <button
          v-if="isAdmin"
          class="sync-btn"
          @click="syncPolar"
          :disabled="syncing"
          title="Polar 메일 동기화"
        >
          {{ syncing ? "동기화 중..." : "동기화" }}
        </button>
        <button
          class="mobile-menu-btn"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
        >
          ☰
        </button>
      </div>
    </header>

    <!-- 모바일 메뉴 추가 -->
    <div class="mobile-menu" :class="{ active: isMobileMenuOpen }">
      <div class="mobile-menu-overlay" @click="isMobileMenuOpen = false"></div>
      <div class="mobile-menu-content">
        <MemberInfoCard v-if="isAdmin" :user="memberCard" />
        <AppSideMenu
          :role="role"
          @logout="logout"
          @menu-click="isMobileMenuOpen = false"
          style="margin-top: 16px"
        />
      </div>
    </div>

    <main class="grid">
      <section class="left">
        <MemberInfoCard v-if="isAdmin" :user="memberCard" />
        <AppSideMenu
          :role="role"
          @logout="logout"
          style="margin-top: 16px"
          v-once
        />
      </section>

      <section class="center">
        <router-view />
      </section>
    </main>

    <footer class="footer">
      <div class="footer-bar">
        <span class="hint">Copyright ⓒ 2025 Cardio Factory</span>
      </div>
    </footer>
  </div>
</template>

<script>
import AppLogo from "@/components/AppLogo.vue";
import MemberInfoCard from "@/components/MemberInfoCard.vue";
import AppSideMenu from "@/components/AppSideMenu.vue";
import { apiJson, api } from "@/lib/api";
import { clearSession, getUser } from "@/lib/session";
import { isAdminRole, signedInLandingPath } from "@/lib/adminAccess";
import { isNavigationFailure } from "vue-router";

export default {
  name: "MainLayout",
  components: {
    AppLogo,
    MemberInfoCard,
    AppSideMenu,
  },
  data() {
    return {
      isMobileMenuOpen: false,
      memberCard: {
        name: "-",
        membership: "-",
        expiredAt: "-",
        remaining: "-",
      },
      syncing: false,
      user: getUser(),
    };
  },
  computed: {
    role() {
      return this.user?.role || "";
    },
    isAdmin() {
      return isAdminRole(this.role);
    },
    homePath() {
      return signedInLandingPath(this.role);
    },
  },
  created() {
    // 코치 전용 화면에서는 회원 대시보드 API를 호출하지 않는다.
    if (this.isAdmin) this.loadMemberCard();
  },
  methods: {
    async loadMemberCard() {
      try {
        const s = await apiJson("/v1/me/summary");
        this.memberCard = s;
      } catch (e) {
        console.error("Failed to load member card:", e);
      }
    },
    // 화면을 먼저 떠나고, 떠났을 때만 세션을 지운다. 쓰던 글이 있는 화면(회원 상세·연락 기록 모달)이 "닫을까요?"에서 [취소]를 고르면
    // 로그아웃하지 않고 그 화면에 남는다. 예전에는 세션을 먼저 지워, 남은 화면의 [저장]이 401로 실패했다(4차 리뷰)
    async logout() {
      const failure = await this.$router.replace("/signin").catch((e) => e);
      if (isNavigationFailure(failure)) return;
      try {
        await api("/v1/auth/sessions", { method: "DELETE" });
      } catch (e) {
        console.error("Logout failed:", e);
      }
      clearSession();
    },
    async syncPolar() {
      if (this.syncing) return;

      if (!confirm("Polar 메일 동기화를 시작하시겠습니까?")) {
        return;
      }

      this.syncing = true;
      try {
        const response = await apiJson("/v1/admin/sync-polar", {
          method: "POST",
        });

        // 성공한 사람들의 리스트 표시
        let message = `동기화가 완료되었습니다.\n\n성공: ${response.successful_count}명`;
        if (response.successful_users && response.successful_users.length > 0) {
          message += `\n\n동기화된 회원:\n${response.successful_users.join(
            ", "
          )}`;
        } else {
          message += "\n\n동기화된 메일이 없습니다.";
        }

        alert(message);
        console.log("동기화 결과:", response);
      } catch (error) {
        console.error("동기화 실패:", error);
        alert("동기화에 실패했습니다. 콘솔을 확인해주세요.");
      } finally {
        this.syncing = false;
      }
    },
  },
};
</script>

<style scoped>
.page {
  --gutter-left: 88px;
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.topbar {
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 var(--gutter-left);
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sync-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #78ff47;
  background: transparent;
  color: #78ff47;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sync-btn:hover:not(:disabled) {
  background: rgba(120, 255, 71, 0.1);
}

.sync-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  width: 100%;
  margin: 24px 0 64px;
  padding: 0 16px 0 var(--gutter-left);
  box-sizing: border-box;
}

.left {
  display: grid;
  gap: 16px;
  align-self: start;
}

.center {
  display: flex;
  flex-direction: column;
}

.footer-bar {
  width: 100%;
  padding: 12px 16px;
  background: #151718;
}

.hint {
  color: #8e8e8e;
  font-size: 14px;
}

@media (max-width: 1100px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.mobile-menu {
  display: none;
}

@media (max-width: 1100px) {
  .mobile-menu-btn {
    display: block;
  }

  .page {
    --gutter-left: 0; /* 모바일에서 왼쪽 여백 제거 */
  }

  .left {
    display: none;
  }

  .mobile-menu {
    display: block;
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1000;
  }

  .mobile-menu.active {
    pointer-events: all;
  }

  .mobile-menu-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .mobile-menu.active .mobile-menu-overlay {
    opacity: 1;
  }

  .mobile-menu-content {
    position: absolute;
    top: 0;
    right: 0;
    width: 320px;
    max-width: 80%;
    height: 100%;
    background: #000;
    padding: 24px 16px;
    transform: translateX(100%);
    transition: transform 0.3s;
    overflow-y: auto;
  }

  .mobile-menu.active .mobile-menu-content {
    transform: translateX(0);
  }
}
</style>
